<template>
  <div class="overflow-hidden">
    <!-- Order Header with gradient background -->
    <div :class="[
      'px-6 py-4',
      isPreorder
        ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200'
        : 'bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200'
    ]">
      <div class="flex justify-between items-start">
        <div class="flex-1">
          <!-- Order Number and Type -->
          <div class="flex items-center gap-3 mb-2">
            <div class="flex items-center gap-2">
              <svg v-if="isPreorder" class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <svg v-else class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <h3 class="text-lg font-semibold text-gray-900">
                {{ isPreorder ? $t('Vorbestellung') : $t('Bestellung') }}
                <span class="font-bold text-gray-800">{{ order.order_number || '#' + order.id }}</span>
              </h3>
            </div>

            <!-- Status Badges -->
            <div class="flex flex-wrap gap-2">
              <span
                v-if="isPreorder"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>
                {{ $t('Vorbestellung') }}
              </span>

              <span
                v-if="order.data?.shipped"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                </svg>
                {{ $t('Versendet') }}
              </span>

              <span
                v-else-if="order.data?.ordered_at && order.data?.payment_method !== 'prepayment'"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clip-rule="evenodd"/>
                </svg>
                {{ $t('In Bearbeitung') }}
              </span>

              <span
                v-if="!isPaid && order.data?.shipped"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                {{ $t('Zahlung ausstehend') }}
              </span>

              <span
                v-if="order.data?.payment_method === 'prepayment' && !isPaid"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
              >
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                {{ $t('Vorkasse erforderlich') }}
              </span>
            </div>
          </div>

          <!-- Date and Payment Info -->
          <div class="flex items-center gap-4 text-sm text-gray-600">
            <div class="flex items-center gap-1">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              {{ formatDate(order.created_at) }}
            </div>
            <div v-if="order.data?.payment_method" class="flex items-center gap-1">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
              </svg>
              {{ $t(getPaymentMethodLabel(order.data.payment_method)) }}
            </div>
          </div>

          <!-- Payment Due Warning -->
          <div v-if="!isPaid && order.data?.due_in !== undefined" class="mt-2">
            <p
              v-if="order.data.due_in >= 0"
              class="flex items-center gap-1 text-sm text-yellow-600"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
              </svg>
              {{ $t('Fällig in :days Tagen', { days: order.data.due_in }) }}
            </p>
            <p
              v-else
              class="flex items-center gap-1 text-sm text-red-600 font-medium"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
              </svg>
              {{ $t('Überfällig seit :days Tagen', { days: Math.abs(order.data.due_in) }) }}
            </p>
          </div>
        </div>

        <!-- Total Amount -->
        <div class="text-right">
          <p class="text-2xl font-bold text-gray-900">
            {{ formatMoney(order.data?.total || 0) }}
            <span class="text-base font-medium text-gray-600">{{ order.data?.currency || 'EUR' }}</span>
          </p>
          <p v-if="isPaid" class="flex items-center justify-end gap-1 text-sm text-green-600 mt-1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            {{ $t('Bezahlt') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6">
      <!-- Pallet Shipping Alert -->
      <div v-if="order.data?.pallet_shipping" class="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-blue-700">
              {{ $t('Paletten-Versand - warten auf Bestimmung der Versandkosten') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Tracking Information -->
      <div v-if="order.data?.tracking_codes?.length" class="mb-4">
        <div class="flex items-center gap-2 mb-2">
          <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
          </svg>
          <p class="text-sm font-medium text-gray-700">{{ $t('Sendungsverfolgung') }}</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <a
            v-for="code in order.data.tracking_codes"
            :key="code"
            :href="`https://nolp.dhl.de/nextt-online-public/set_identcodes.do?idc=${code}`"
            target="_blank"
            class="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-200 transition-colors"
          >
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
            </svg>
            {{ code }}
          </a>
        </div>
      </div>

      <!-- Order Items -->
      <div class="mb-4">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
          </svg>
          <p class="text-sm font-medium text-gray-700">{{ $t('Bestellte Artikel') }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-4 space-y-2">
          <div
            v-for="item in order.data?.items || []"
            :key="item.name"
            class="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
          >
            <div class="flex items-center gap-3">
              <span class="inline-flex items-center justify-center w-8 h-8 bg-white rounded-full text-sm font-medium text-gray-700 border border-gray-300">
                {{ item.quantity }}×
              </span>
              <div>
                <span class="text-sm text-gray-900 font-medium" v-html="item.name"></span>
                <span v-if="item.sku" class="text-xs text-gray-500 ml-2">({{ item.sku }})</span>
              </div>
            </div>
            <span v-if="item.price" class="text-sm text-gray-700 font-medium">
              {{ formatMoney(item.price * item.quantity) }} {{ order.data?.currency }}
            </span>
          </div>
          <div v-if="!order.data?.items || order.data.items.length === 0" class="text-sm text-gray-500 italic">
            {{ $t('Keine Artikel verfügbar') }}
          </div>
        </div>
      </div>

      <!-- Correction Invoices -->
      <div v-if="order.children?.length" class="mb-4">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p class="text-sm font-medium text-gray-700">{{ $t('Korrekturrechnungen') }}</p>
        </div>
        <div class="bg-yellow-50 rounded-lg overflow-hidden border border-yellow-200">
          <table class="min-w-full divide-y divide-yellow-200">
            <thead class="bg-yellow-100">
              <tr>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">{{ $t('Rechnungsnummer') }}</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">{{ $t('Betrag') }}</th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-700 uppercase">{{ $t('Aktion') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-yellow-200 bg-white">
              <tr v-for="child in order.children" :key="child.id">
                <td class="px-4 py-2 text-sm text-gray-900">{{ child.order_number }}</td>
                <td class="px-4 py-2 text-sm text-gray-900">{{ formatMoney(child.data?.total || 0) }} {{ child.data?.currency }}</td>
                <td class="px-4 py-2 text-right">
                  <a
                    v-if="child.data?.invoice_url"
                    :href="child.data.invoice_url"
                    target="_blank"
                    class="inline-flex items-center gap-1 text-sonnenglas-600 hover:text-sonnenglas-700 text-sm font-medium"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                    </svg>
                    {{ $t('Herunterladen') }}
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
        <!-- Primary Actions -->
        <a
          v-if="order.data?.invoice_url"
          :href="order.data.invoice_url"
          target="_blank"
          class="inline-flex items-center gap-2 bg-sonnenglas-600 text-white px-4 py-2 rounded-lg hover:bg-sonnenglas-700 transition-colors text-sm font-medium shadow-sm"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          {{ order.data?.proforma ? $t('Proforma-Rechnung') : $t('Rechnung herunterladen') }}
        </a>

        <!-- Secondary Actions -->
        <button
          v-if="!isPaid"
          @click="showBankDetails = !showBankDetails"
          class="inline-flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors text-sm font-medium"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
          </svg>
          {{ showBankDetails ? $t('Bankverbindung ausblenden') : $t('Bankverbindung anzeigen') }}
        </button>

        <Link
          v-if="route"
          :href="route('contactForm') + '?order_number=' + order.order_number"
          class="inline-flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors text-sm font-medium"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
          </svg>
          {{ $t('Frage stellen') }}
        </Link>

        <Link
          v-if="route"
          :href="route('orders.reorder', order.order_number)"
          method="post"
          :title="$t('Die gleichen Artikel erneut bestellen')"
          class="inline-flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors text-sm font-medium"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l-2.5-5M9 19.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM20.5 19.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
          </svg>
          {{ $t('Nachbestellen') }}
        </Link>

        <a
          v-if="isPreorder"
          :href="`cancel?order_number=${order.order_number}`"
          class="inline-flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 border border-red-300 transition-colors text-sm font-medium"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          {{ $t('Stornieren') }}
        </a>
      </div>

      <!-- Bank Details (Collapsible) -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          v-if="showBankDetails && !isPaid"
          class="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
              <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd"/>
            </svg>
            <div class="flex-1">
              <h4 class="font-medium text-blue-900 mb-3">{{ $t('Bankverbindung für Überweisung') }}</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-blue-700 font-medium">{{ $t('Empfänger') }}</p>
                  <p class="text-blue-900">{{ bankDetails.holder || 'Sonnenglas GmbH' }}</p>
                </div>
                <div>
                  <p class="text-blue-700 font-medium">{{ $t('IBAN') }}</p>
                  <p class="text-blue-900 font-mono">{{ bankDetails.iban || 'DE03 6205 0000 0001 0088 74' }}</p>
                </div>
                <div>
                  <p class="text-blue-700 font-medium">{{ $t('BIC') }}</p>
                  <p class="text-blue-900 font-mono">{{ bankDetails.bic || 'HEISDE66XXX' }}</p>
                </div>
                <div>
                  <p class="text-blue-700 font-medium">{{ $t('Bank') }}</p>
                  <p class="text-blue-900">{{ bankDetails.bank || 'Kreissparkasse Heilbronn' }}</p>
                </div>
                <div class="sm:col-span-2">
                  <p class="text-blue-700 font-medium">{{ $t('Verwendungszweck') }}</p>
                  <p class="text-blue-900 font-mono bg-white px-2 py-1 rounded border border-blue-300 inline-block mt-1">
                    {{ order.order_number || 'Order #' + order.id }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue'
import { Link } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'

// Inject Ziggy route helper for script setup
const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

interface OrderItem {
  quantity: number
  name: string
  sku?: string
  price?: number
}

interface Order {
  id: number
  order_number: string
  created_at: string
  data?: {
    items?: OrderItem[]
    total?: number
    currency?: string
    shipped?: boolean
    shipped_at?: string
    ordered_at?: string
    payment_method?: string
    tracking_codes?: string[]
    proforma?: boolean
    invoice_url?: string
    pallet_shipping?: boolean
    due_in?: number
    status?: string
  }
  children?: Order[]
  is_paid?: boolean
}

interface Props {
  order: Order
  isPreorder: boolean
}

const props = defineProps<Props>()

const showBankDetails = ref(false)

const isPaid = computed(() => {
  return props.order.is_paid ?? false
})

// Compute bank details based on order data
const bankDetails = computed(() => {
  // Check if order has bank details from API
  if (props.order.data?.bank_details) {
    return props.order.data.bank_details
  }

  // Default to German bank account from config
  return {
    holder: 'Sonnenglas GmbH',
    iban: 'DE03 6205 0000 0001 0088 74',
    bic: 'HEISDE66XXX',
    bank: 'Kreissparkasse Heilbronn'
  }
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatMoney = (amount: number) => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

// Payment method display mapping - returns translation keys
const getPaymentMethodLabel = (method: string) => {
  const methodKeys: Record<string, string> = {
    'prepayment': trans('Vorkasse'),
    'prepaid': trans('Vorkasse'),
    'invoice': trans('Rechnung'),
    'creditcard': trans('Kreditkarte'),
    'paypal': 'PayPal',
    'sepa': trans('SEPA Lastschrift')
  }
  return methodKeys[method] || method
}
</script>